import React from "react";
import { cn } from "~/lib/utils";

interface MenuButtonProps {
  onClick: (e?: React.MouseEvent) => void;
  label: string;
  ariaLabel?: string;
  className?: string;
  isSelected?: boolean;

  // Left side content
  leftIcon?: React.ReactNode;

  // Checkmark options
  checkmark?: "left" | "right" | false;

  // Right side content
  rightContent?: React.ReactNode;
  rightAction?: {
    icon: React.ReactNode;
    onClick: (e: React.MouseEvent) => void;
    title: string;
    className?: string;
  };
}

export function MenuButton({
  onClick,
  label,
  ariaLabel,
  className,
  isSelected = false,
  leftIcon,
  checkmark = false,
  rightContent,
  rightAction,
}: MenuButtonProps) {
  const CheckmarkIcon = () => (
    <svg className="size-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  const renderLeftContent = () => {
    if (checkmark === "left") {
      return (
        <div className="mr-2 w-4 flex justify-center">
          {isSelected ? <CheckmarkIcon /> : leftIcon}
        </div>
      );
    }

    if (leftIcon) {
      return <div className="mr-2">{leftIcon}</div>;
    }

    return null;
  };

  const renderRightContent = () => {
    if (checkmark === "right") {
      return (
        <div className="ml-2 w-4 flex justify-center">
          {isSelected ? <CheckmarkIcon /> : rightContent}
        </div>
      );
    }

    if (rightContent) {
      return <div className="ml-2">{rightContent}</div>;
    }

    return null;
  };

  return (
    <div
      className={cn(
        "relative group flex items-center justify-between px-2 py-2 text-[13px] cursor-pointer rounded",
        isSelected && !checkmark ? "bg-gray-200/70 hover:bg-gray-200/70" : "hover:bg-gray-100",
        className
      )}
      onClick={(e) => onClick(e)}
      aria-label={ariaLabel}
    >
      <div className="flex items-center overflow-hidden">
        {renderLeftContent()}
        <p className="truncate">{label}</p>
      </div>

      <div className="flex items-center">
        {renderRightContent()}
        {rightAction && (
          <button
            className={cn(
              "opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded ml-2 cursor-pointer",
              rightAction.className
            )}
            onClick={rightAction.onClick}
            title={rightAction.title}
          >
            {rightAction.icon}
          </button>
        )}
      </div>
    </div>
  );
}
